/**
 * Documenting and correcting a safety flag (DECISIONS.md #46).
 *
 * Isolation, infection risk, fall risk, weight, and *allergy status not established* — the five types
 * a clinician can create here. **DNR and treatment limitation cannot** (#46 point 4): the record
 * requires who took the decision and when, there is nowhere to put those in phase 0, and a DNR whose
 * decider the record cannot name is worse than a DNR that is not in this system at all. They are left
 * out of the picker rather than offered and refused, and {@link SafetyFlagForm} says so in the
 * explanation above the fields so a clinician looking for DNR is not left hunting.
 *
 * **Weight crosses the wire as the text that was typed** and is refused rather than repaired
 * (#46 point 6). `62,4` is not silently read as `62.4`: a comma is the decimal separator in Swedish
 * and the factor-of-ten risk that `I18N.md` §4 exists to prevent is exactly a number whose separator
 * somebody guessed at. The hint under the box says which one to use before the refusal has to.
 *
 * Otherwise the same form as {@link AllergyForm} — no draft module for the same reason, the same
 * unsaved-work guards, the same append-only sentence over the button.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { PatientSafetyFlag } from './patientOverviewApi'
import {
  flagRefusalMessage,
  flagStatusLabel,
  flagTakesValue,
  flagTypeLabel,
  flagValueLabel,
  safetyUnavailableMessage,
} from './safetyDisplay'
import {
  correctSafetyFlag,
  documentSafetyFlag,
  DOCUMENTABLE_FLAG_TYPES,
  FlagRefusedError,
  FlagUnavailableError,
  type FlagRefusalCode,
  type FlagRefusalParams,
  type SafetyFlagSubmission,
  type WrittenSafetyFlag,
} from './safetyFlagApi'

/**
 * The statuses a correction offers. A create offers none.
 *
 * A first version is always `active`, because the server refuses the other two
 * (`FLAG_STATUS_NOT_ALLOWED`) and it is right to: a flag created already resolved, or created in
 * order to say it was a mistake, documents nothing that happened. So there is no select on a create
 * rather than a select with one option in it.
 */
const CORRECTION_STATUSES = ['active', 'resolved', 'entered-in-error'] as const

/** Mirrors `SafetyFlagContent`; see {@link AllergyForm}'s equivalent for why it is not load-bearing. */
const MAXIMUM_VALUE_LENGTH = 200

/** The note is the reasoning behind a flag, and it is the longer of the two. */
const MAXIMUM_NOTE_LENGTH = 2000

/** The form's fields, as text. */
interface FlagDraft {
  readonly flagType: string
  readonly status: string
  readonly value: string
  readonly note: string
}

/** See {@link AllergyForm}'s equivalent for why this is derived from the prop rather than copied. */
function draftFrom(flag: PatientSafetyFlag | null): FlagDraft {
  return {
    // The first documentable type, so the value field's label is never the generic one on open. A
    // correction takes the type from the record and cannot change it (#46 point 2).
    flagType: flag?.flagType ?? DOCUMENTABLE_FLAG_TYPES[0],
    // `active` either way, and the overview carries no status to read it from — deliberately: it
    // returns only the flags that are current, so a flag reachable from the panel is an active one.
    // A correction that leaves the select alone therefore re-asserts what the record already says,
    // and the server answers `FLAG_NO_CHANGE` if that is all it does.
    status: 'active',
    value: flag?.value ?? '',
    note: flag?.note ?? '',
  }
}

function isDirty(draft: FlagDraft, original: FlagDraft): boolean {
  return (
    draft.flagType !== original.flagType ||
    draft.status !== original.status ||
    draft.value !== original.value ||
    draft.note !== original.note
  )
}

function orNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

function toSubmission(draft: FlagDraft, supersedes: string | null): SafetyFlagSubmission {
  return {
    supersedes,
    // Null on a correction. Sending the type a correction inherits would be
    // `FLAG_TYPE_NOT_ALLOWED` — the server will not take a field it is going to ignore, because a
    // field that is accepted and ignored reads as a field that was applied.
    flagType: supersedes === null ? draft.flagType : null,
    status: draft.status,
    // Untrimmed for a weight, deliberately: it is refused, never repaired (#46 point 6), and
    // trimming is the first step onto the slope that ends at reading `62,4` as `62.4`. The other
    // types are free text where a stray space is noise rather than meaning.
    value: flagTakesValue(draft.flagType)
      ? draft.flagType === 'weight'
        ? draft.value
        : orNull(draft.value)
      : null,
    note: orNull(draft.note),
  }
}

type Phase =
  | { readonly kind: 'editing' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'written' }

type Notice =
  | {
      readonly kind: 'refused'
      readonly code: FlagRefusalCode | 'UNKNOWN'
      readonly params: FlagRefusalParams
    }
  | { readonly kind: 'unavailable' }

/**
 * The safety-flag form, for a first version or for a correction.
 *
 * @param patientId Whose record this is written to.
 * @param flag The flag being corrected, or null to document one. Read on every render.
 * @param onWritten The row was written; the overview refetches.
 * @param onStale Somebody else corrected this flag. Refetches and keeps the draft.
 * @param onCorrectExisting The patient already has a flag of this type, and the server named it
 *   (#46 point 10). Reopens the form as a correction of that flag, which is the thing the clinician
 *   was trying to do — a bare refusal would leave them to find the row themselves and guess which of
 *   two truths the panel is showing.
 * @param onClose Leaves the form.
 */
export function SafetyFlagForm({
  patientId,
  flag,
  onWritten,
  onStale,
  onCorrectExisting,
  onClose,
}: {
  readonly patientId: string
  readonly flag: PatientSafetyFlag | null
  readonly onWritten: (written: WrittenSafetyFlag) => void
  readonly onStale: () => void
  readonly onCorrectExisting: (flagId: string) => void
  readonly onClose: () => void
}) {
  const { t } = useTranslation(['common', 'patient', 'safety'])

  const correcting = flag !== null

  const [phase, setPhase] = useState<Phase>({ kind: 'editing' })
  const [draft, setDraft] = useState<FlagDraft>(() => draftFrom(flag))
  const [notice, setNotice] = useState<Notice | null>(null)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)

  /** See {@link AllergyForm}: a submit in flight is not aborted on unmount. */
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const original = draftFrom(flag)
  const dirty = isDirty(draft, original)
  const busy = phase.kind === 'saving'

  useEffect(() => {
    if (!dirty || phase.kind === 'written') {
      return
    }

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()

      // Both, for the reason `NoteEditor` gives.
      event.returnValue = 'unsaved'
    }

    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, phase.kind])

  const update = <Field extends keyof FlagDraft>(field: Field, value: string) => {
    setDraft((previous) => ({ ...previous, [field]: value }))
    setNotice(null)
  }

  const submit = () => {
    if (busy) {
      return
    }

    setPhase({ kind: 'saving' })
    setNotice(null)

    const controller = new AbortController()
    const submission = toSubmission(draft, flag?.versionId ?? null)

    const written = correcting
      ? correctSafetyFlag(patientId, flag.flagId, submission, controller.signal)
      : documentSafetyFlag(patientId, submission, controller.signal)

    written.then(
      (result) => {
        if (!live.current) {
          return
        }
        setPhase({ kind: 'written' })
        onWritten(result)
      },
      (cause: unknown) => {
        if (!live.current) {
          return
        }

        setPhase({ kind: 'editing' })

        if (cause instanceof FlagRefusedError) {
          setNotice({ kind: 'refused', code: cause.code, params: cause.params })

          if (cause.code === 'FLAG_VERSION_STALE') {
            onStale()
          }
        } else if (cause instanceof FlagUnavailableError) {
          setNotice({ kind: 'unavailable' })
        } else {
          setNotice({ kind: 'refused', code: 'UNKNOWN', params: {} })
        }
      },
    )
  }

  const close = () => {
    if (dirty && phase.kind === 'editing') {
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  const heading = correcting ? t('safety:flag.headingCorrect') : t('safety:flag.headingDocument')

  if (phase.kind === 'written') {
    return (
      <section className="registration patient-correction" aria-labelledby="flag-form-heading">
        <h3 id="flag-form-heading">{heading}</h3>

        <p className="registration__registered" role="status">
          {correcting ? t('safety:flag.savedCorrect') : t('safety:flag.savedDocument')}
        </p>

        <button type="button" onClick={onClose}>
          {t('safety:form.close')}
        </button>
      </section>
    )
  }

  /**
   * The existing flag the server named, when this create collided with one.
   *
   * Only for `FLAG_ALREADY_DOCUMENTED`, and only when the id actually arrived: the offer to correct
   * it addresses that id, and a button that reopens the form against nothing would be worse than no
   * button.
   */
  const collidingFlagId =
    notice !== null &&
    notice.kind === 'refused' &&
    notice.code === 'FLAG_ALREADY_DOCUMENTED' &&
    notice.params.flagId !== undefined
      ? notice.params.flagId
      : null

  const takesValue = flagTakesValue(draft.flagType)

  return (
    <section className="registration patient-correction" aria-labelledby="flag-form-heading">
      <header className="registration__header">
        <h3 id="flag-form-heading">{heading}</h3>

        <p className="registration__explains">
          {correcting ? t('safety:flag.explanationCorrect') : t('safety:flag.explanationDocument')}
        </p>
      </header>

      {correcting ? (
        // The type as a sentence rather than a disabled select. It cannot be changed at all
        // (#46 point 2) — a correction says something different about the same fact, and a flag of
        // the wrong type is retracted and documented again, which the explanation above says.
        <div className="registration__field">
          <span className="registration__hint">{t('safety:flag.fieldType')}</span>
          <p>{flagTypeLabel(draft.flagType, t)}</p>
        </div>
      ) : (
        <div className="registration__field">
          <label htmlFor="flag-field-type">{t('safety:flag.fieldType')}</label>

          <p id="flag-field-type-hint" className="registration__hint">
            {t('safety:flag.hintType')}
          </p>

          <select
            id="flag-field-type"
            value={draft.flagType}
            disabled={busy}
            autoFocus
            aria-describedby="flag-field-type-hint"
            onChange={(event) => update('flagType', event.target.value)}
          >
            {/* The five of seven that can be created. DNR and treatment limitation are absent, not
                disabled: an option that cannot be chosen is a question the clinician has to ask
                somebody, and the explanation above already answers it. */}
            {DOCUMENTABLE_FLAG_TYPES.map((flagType) => (
              <option key={flagType} value={flagType}>
                {flagTypeLabel(flagType, t)}
              </option>
            ))}
          </select>
        </div>
      )}

      {correcting && (
        <div className="registration__field">
          <label htmlFor="flag-field-status">{t('safety:flag.fieldStatus')}</label>

          <p id="flag-field-status-hint" className="registration__hint">
            {t('safety:flag.hintStatus')}
          </p>

          <select
            id="flag-field-status"
            value={draft.status}
            disabled={busy}
            aria-describedby="flag-field-status-hint"
            onChange={(event) => update('status', event.target.value)}
          >
            {CORRECTION_STATUSES.map((status) => (
              <option key={status} value={status}>
                {flagStatusLabel(status, t)}
              </option>
            ))}
          </select>
        </div>
      )}

      {takesValue && (
        <div className="registration__field">
          {/* The label is the type's own word for its value — *Vikt i kilogram*, *Typ av isolering*,
              *Begränsningen i korthet* — because "Uppgift" over a weight box tells a clinician
              nothing about what unit to use. */}
          <label htmlFor="flag-field-value">{flagValueLabel(draft.flagType, t)}</label>

          <p id="flag-field-value-hint" className="registration__hint">
            {draft.flagType === 'weight'
              ? t('safety:flag.hintWeight')
              : draft.flagType === 'isolation'
                ? t('safety:flag.hintIsolationType')
                : t('safety:flag.hintLimitation')}
          </p>

          <input
            id="flag-field-value"
            type="text"
            // `text`, not `number`, for the weight. A number input's value is the browser's idea of
            // the number: a comma is either swallowed or silently reinterpreted depending on locale,
            // the spinner invites a drag that changes a clinical value by accident, and the whole
            // point of #46 point 6 is that the record sees what was typed and refuses it itself.
            value={draft.value}
            disabled={busy}
            aria-describedby="flag-field-value-hint"
            maxLength={MAXIMUM_VALUE_LENGTH}
            onChange={(event) => update('value', event.target.value)}
          />
        </div>
      )}

      <div className="registration__field">
        <label htmlFor="flag-field-note">
          {t('safety:flag.fieldNote')}
          <span className="registration__optional"> ({t('safety:form.optional')})</span>
        </label>

        <p id="flag-field-note-hint" className="registration__hint">
          {t('safety:flag.hintNote')}
        </p>

        <textarea
          id="flag-field-note"
          value={draft.note}
          disabled={busy}
          rows={3}
          aria-describedby="flag-field-note-hint"
          maxLength={MAXIMUM_NOTE_LENGTH}
          onChange={(event) => update('note', event.target.value)}
        />
      </div>

      <div aria-live="assertive">
        {notice !== null && (
          <div className="registration__notice" role="alert">
            <p>
              {notice.kind === 'unavailable'
                ? safetyUnavailableMessage(t)
                : flagRefusalMessage(notice.code, notice.params, t)}
            </p>

            {collidingFlagId !== null && (
              <button type="button" onClick={() => onCorrectExisting(collidingFlagId)}>
                {t('safety:flag.correctExisting')}
              </button>
            )}
          </div>
        )}
      </div>

      <footer className="registration__actions">
        <p className="registration__final">{t('safety:form.appendOnly')}</p>

        <button
          type="button"
          className="registration__register"
          disabled={busy}
          onClick={submit}
        >
          {busy ? t('safety:form.saving') : t('safety:form.save')}
        </button>

        <button type="button" disabled={busy} onClick={close}>
          {t('safety:form.cancel')}
        </button>
      </footer>

      {confirmingDiscard && (
        <div
          className="registration__discard"
          role="alertdialog"
          aria-label={t('safety:form.discardQuestion')}
        >
          <p>{t('safety:form.discardQuestion')}</p>

          <button type="button" onClick={() => setConfirmingDiscard(false)}>
            {t('safety:form.discardCancel')}
          </button>
          <button type="button" onClick={onClose}>
            {t('safety:form.discardConfirm')}
          </button>
        </div>
      )}
    </section>
  )
}
